/**
 * Auth Provider Utilities
 *
 * Helpers for detecting the authentication provider used by a user.
 * Used to implement provider-aware routing logic:
 * - OAuth users (Google, Microsoft) skip email verification
 * - Password users require email verification
 */

import type { User } from 'firebase/auth';

/**
 * OAuth provider IDs recognized by Firebase
 */
export const OAUTH_PROVIDERS = ['google.com', 'microsoft.com'] as const;
export type OAuthProviderId = (typeof OAUTH_PROVIDERS)[number];

/**
 * Password provider ID
 */
export const PASSWORD_PROVIDER = 'password';

/**
 * Check if a user signed in via OAuth (Google or Microsoft)
 *
 * OAuth users have their email automatically verified by the provider,
 * so they should skip email verification gates.
 *
 * @param user - Firebase Auth user object
 * @returns true if user signed in via Google or Microsoft OAuth
 */
export function isOAuthUser(user: User | null): boolean {
  if (!user) return false;

  // Check providerData for OAuth providers
  return user.providerData.some((provider) =>
    OAUTH_PROVIDERS.includes(provider.providerId as OAuthProviderId)
  );
}

/**
 * Check if a user signed in via email/password
 *
 * Password users must verify their email before accessing the app.
 *
 * @param user - Firebase Auth user object
 * @returns true if user signed in via email/password
 */
export function isPasswordUser(user: User | null): boolean {
  if (!user) return false;

  // Check providerData for password provider
  return user.providerData.some((provider) => provider.providerId === PASSWORD_PROVIDER);
}

/**
 * Get the primary auth provider for a user
 *
 * Returns the first provider found in order of precedence:
 * 1. Google
 * 2. Microsoft
 * 3. Password
 * 4. Unknown
 *
 * @param user - Firebase Auth user object
 * @returns Provider ID string
 */
export function getPrimaryProvider(user: User | null): string {
  if (!user) return 'unknown';

  for (const provider of user.providerData) {
    if (provider.providerId === 'google.com') return 'google.com';
    if (provider.providerId === 'microsoft.com') return 'microsoft.com';
    if (provider.providerId === 'password') return 'password';
  }

  return 'unknown';
}

/**
 * Check if user's email verification is satisfied.
 *
 * Firebase may reset emailVerified to false when a password provider is linked
 * to an existing OAuth account. Google/Microsoft users are verified by definition
 * since those providers verify email before issuing tokens.
 *
 * @param user - Firebase Auth user object
 * @returns true if emailVerified === true OR user has OAuth provider
 */
export function isEffectivelyVerified(user: User | null): boolean {
  if (!user) return false;

  return (
    user.emailVerified ||
    user.providerData.some((p) => p.providerId === 'google.com' || p.providerId === 'microsoft.com')
  );
}
